import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { HorizonCloudScopePicker } from './HorizonCloudScopePicker';

type SourceDescriptor = {
  id: string;
  title: string;
  description: string;
  mode: 'synthetic' | 'connected';
  availability: 'available' | 'not-configured';
};

const cloudScopeSource: SourceDescriptor = {
  id: 'cloud',
  title: 'Cloud platform',
  description: 'Create a frozen multi-account cloud assessment scope.',
  mode: 'connected',
  availability: 'available',
};

type DiscoveryPathItem = {
  levelId: string;
  optionId: string;
};

type DiscoveryOption = {
  id: string;
  title: string;
  description?: string;
};

type DiscoveryLevel = {
  id: string;
  title: string;
  description?: string;
  options: DiscoveryOption[];
};

type DiscoveryResponse =
  | { complete: false; level: DiscoveryLevel }
  | {
      complete: true;
      selection: { workloadRef: string; workloadTitle: string };
    };

type SelectedLevel = DiscoveryLevel & { selectedOptionId: string };

function emptyLevelMessage(level: DiscoveryLevel) {
  if (level.id === 'workload') {
    return 'No eligible EC2 servers/workloads found in this VPC.';
  }
  if (level.id === 'vpc') {
    return 'No VPCs found in this Region.';
  }
  return `No ${level.title.toLocaleLowerCase('en-US')} options are available.`;
}

function discoveryOptionLabel(option: DiscoveryOption) {
  return option.description
    ? `${option.title} — ${option.description}`
    : option.title;
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      // Keep the status-only message when an intermediary returns non-JSON.
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

function isSourcesResponse(
  value: unknown,
): value is { sources: SourceDescriptor[] } {
  if (typeof value !== 'object' || value === null || !('sources' in value)) {
    return false;
  }
  const { sources } = value as { sources: unknown };
  return (
    Array.isArray(sources) &&
    sources.every(
      source =>
        typeof source === 'object' &&
        source !== null &&
        typeof (source as SourceDescriptor).id === 'string' &&
        typeof (source as SourceDescriptor).title === 'string' &&
        typeof (source as SourceDescriptor).description === 'string' &&
        ['synthetic', 'connected'].includes(
          (source as SourceDescriptor).mode,
        ) &&
        ['available', 'not-configured'].includes(
          (source as SourceDescriptor).availability,
        ),
    )
  );
}

function isDiscoveryResponse(value: unknown): value is DiscoveryResponse {
  if (typeof value !== 'object' || value === null || !('complete' in value)) {
    return false;
  }
  const candidate = value as Partial<DiscoveryResponse>;
  if (candidate.complete === true) {
    return Boolean(
      candidate.selection &&
        typeof candidate.selection.workloadRef === 'string' &&
        typeof candidate.selection.workloadTitle === 'string',
    );
  }
  if (candidate.complete === false) {
    return Boolean(
      candidate.level &&
        typeof candidate.level.id === 'string' &&
        typeof candidate.level.title === 'string' &&
        Array.isArray(candidate.level.options) &&
        candidate.level.options.every(
          option =>
            typeof option === 'object' &&
            option !== null &&
            typeof option.id === 'string' &&
            typeof option.title === 'string' &&
            (option.description === undefined ||
              typeof option.description === 'string'),
        ),
    );
  }
  return false;
}

export function HorizonWorkloadPicker(
  props: FieldExtensionComponentProps<string>,
) {
  const { formData, onChange, rawErrors, required, disabled, readonly } = props;
  const fieldId = useId();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const requestSequence = useRef(0);
  const [sources, setSources] = useState<SourceDescriptor[]>([]);
  const [sourceId, setSourceId] = useState(
    formData?.startsWith('asscope-') ? 'cloud' : '',
  );
  const [levels, setLevels] = useState<SelectedLevel[]>([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSources = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('horizon-source');
      const response = await readJson<unknown>(
        await fetchApi.fetch(`${baseUrl}/v1/sources`),
      );
      if (!isSourcesResponse(response)) {
        throw new Error('The source service returned an invalid response');
      }
      if (sequence === requestSequence.current) {
        const otherSources = response.sources.filter(
          source => source.id !== 'cloud',
        );
        setSources([...otherSources, cloudScopeSource]);
      }
    } catch (caught) {
      if (sequence === requestSequence.current) {
        setSources([cloudScopeSource]);
        setError(
          caught instanceof Error ? caught.message : 'Unable to load sources',
        );
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  const discover = useCallback(
    async (nextSourceId: string, path: DiscoveryPathItem[]) => {
      const sequence = ++requestSequence.current;
      setLoading(true);
      setError('');
      try {
        const baseUrl = await discoveryApi.getBaseUrl('horizon-source');
        const response = await readJson<unknown>(
          await fetchApi.fetch(`${baseUrl}/v1/discover`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sourceId: nextSourceId, path }),
          }),
        );
        if (!isDiscoveryResponse(response)) {
          throw new Error('The source service returned an invalid response');
        }
        return sequence === requestSequence.current ? response : undefined;
      } catch (caught) {
        if (sequence === requestSequence.current) {
          setError(
            caught instanceof Error
              ? caught.message
              : 'Unable to browse this source',
          );
        }
        return undefined;
      } finally {
        if (sequence === requestSequence.current) setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  useEffect(() => {
    void fetchSources();
    return () => {
      requestSequence.current += 1;
    };
  }, [fetchSources]);

  const chooseSource = async (nextSourceId: string) => {
    setSourceId(nextSourceId);
    setLevels([]);
    setSelectedTitle('');
    setError('');
    onChange('');
    if (!nextSourceId) return;
    if (nextSourceId === 'cloud') return;

    const source = sources.find(candidate => candidate.id === nextSourceId);
    if (!source || source.availability !== 'available') {
      setSourceId('');
      setError(
        source
          ? `${source.title} is not configured for your tenant.`
          : 'The selected inventory source is not available.',
      );
      return;
    }

    const response = await discover(nextSourceId, []);
    if (response && !response.complete) {
      setLevels([{ ...response.level, selectedOptionId: '' }]);
    }
  };

  const chooseOption = async (levelIndex: number, optionId: string) => {
    const retained = levels.slice(0, levelIndex + 1).map((level, index) => ({
      ...level,
      selectedOptionId:
        index === levelIndex ? optionId : level.selectedOptionId,
    }));
    setLevels(retained);
    setSelectedTitle('');
    onChange('');
    if (!optionId) return;

    const path = retained.map(level => ({
      levelId: level.id,
      optionId: level.selectedOptionId,
    }));
    const response = await discover(sourceId, path);
    if (!response) return;
    if (response.complete) {
      setSelectedTitle(response.selection.workloadTitle);
      onChange(response.selection.workloadRef);
      return;
    }
    setLevels([...retained, { ...response.level, selectedOptionId: '' }]);
  };

  const retry = async () => {
    if (!sourceId) {
      await fetchSources();
      return;
    }
    const path = levels
      .filter(level => level.selectedOptionId)
      .map(level => ({
        levelId: level.id,
        optionId: level.selectedOptionId,
      }));
    const response = await discover(sourceId, path);
    if (!response) return;
    if (response.complete) {
      setSelectedTitle(response.selection.workloadTitle);
      onChange(response.selection.workloadRef);
      return;
    }
    const selectedLevels = levels.filter(level => level.selectedOptionId);
    setLevels([...selectedLevels, { ...response.level, selectedOptionId: '' }]);
  };

  const locked = Boolean(disabled || readonly || loading);
  const hasAvailableSource = sources.some(
    source => source.availability === 'available',
  );

  return (
    <Box>
      <TextField
        id={`${fieldId}-source`}
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        fullWidth
        required={required}
        disabled={locked}
        label="Inventory source"
        value={sourceId}
        onChange={event => void chooseSource(event.target.value)}
        helperText="Choose where Horizon should find the server. Connections are configured by an administrator."
        margin="normal"
        variant="outlined"
      >
        <option value="">Select a source</option>
        {sources.map(source => (
          <option
            key={source.id}
            value={source.id}
            disabled={source.availability !== 'available'}
          >
            {source.title}
            {source.availability === 'not-configured'
              ? ' — Not configured'
              : ''}
          </option>
        ))}
      </TextField>

      {sourceId === 'cloud' && (
        <HorizonCloudScopePicker
          value={formData ?? ''}
          onChange={onChange}
          disabled={Boolean(disabled || readonly)}
        />
      )}

      {sourceId !== 'cloud' &&
        levels.map((level, index) => (
          <TextField
            key={`${index}-${level.id}`}
            id={`${fieldId}-${index}-${level.id}`}
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            disabled={locked || level.options.length === 0}
            label={level.title}
            value={level.selectedOptionId}
            onChange={event => void chooseOption(index, event.target.value)}
            helperText={
              level.options.length === 0
                ? emptyLevelMessage(level)
                : level.description
            }
            margin="normal"
            variant="outlined"
          >
            {level.options.length > 0 && (
              <option value="">
                Select {level.title.toLocaleLowerCase('en-US')}
              </option>
            )}
            {level.options.map(option => (
              <option key={option.id} value={option.id}>
                {discoveryOptionLabel(option)}
              </option>
            ))}
          </TextField>
        ))}

      {loading && (
        <Box display="flex" alignItems="center" gridGap={8} mt={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading available choices…</Typography>
        </Box>
      )}

      {error && (
        <Box mt={1}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Button color="primary" size="small" onClick={() => void retry()}>
            Retry
          </Button>
        </Box>
      )}

      {!loading && !error && !hasAvailableSource && (
        <Typography color="error" variant="body2">
          No inventory source is configured for your tenant. Contact the Horizon
          administrator.
        </Typography>
      )}

      {formData && sourceId !== 'cloud' && (
        <Box mt={1}>
          <Typography variant="body2">
            {selectedTitle ? (
              <>
                Selected workload: <strong>{selectedTitle}</strong>
              </>
            ) : (
              'A previously selected workload is retained. Choose an inventory source only to change it.'
            )}
          </Typography>
        </Box>
      )}

      {rawErrors?.length ? (
        <Typography color="error" variant="caption">
          {rawErrors.join('; ')}
        </Typography>
      ) : null}
    </Box>
  );
}
